import Boboloading from '@/components/Boboloading';
import {
  deviceId,
  getEncrypted,
  getStage,
  getToken,
  version,
} from '@/utils/func';
import { getTransId, PLATFORMTYPE } from '@/utils/userAgent';
import { Toast } from 'antd-mobile';
import { PayCodeStatus } from './enums/pay';
import { AppUtil, isApp } from './utils/appHooks';
import { decryptData } from './utils/encrypt';
// 与后端约定的响应数据格式
// code 白名单
const codeWhiteList = [
  PayCodeStatus.unnamed,
  PayCodeStatus.inconformity,
  PayCodeStatus.invalidOrder,
];
interface ResponseStructure {
  data: any;
  code: number;
  message: string;
}

export const networkError = (status: number, defaultToast?: string) => {
  switch (status) {
    case 100:
      Toast.show({ content: '请继续请求', icon: 'error' });
      break;
    case 101:
      Toast.show({ content: '请升级协议', icon: 'fail' });
      break;
    case 400:
      Toast.show({ content: '请求错误，服务器未能理解请求', icon: 'fail' });
      break;
    case 401:
      Toast.show({ content: '用户没有权限或登陆超时', icon: 'fail' });
      break;
    case 403:
      Toast.show({ content: '系统维护中', icon: 'fail' });
      break;
    case 404:
      Toast.show({ content: '请求的接口不存在', icon: 'fail' });
      break;
    case 405:
      Toast.show({ content: '请求指定的方法不被允许', icon: 'fail' });
      break;
    case 408:
      Toast.show({ content: '请求超时', icon: 'fail' }); // 408
      break;
    case 429:
      Toast.show({ content: '操作频繁' });
      break;
    case 500:
      Toast.show({ content: '网络错误', icon: 'fail' });
      break;
    case 502:
      Toast.show({ content: '网关错误', icon: 'fail' }); // 502
      break;
    case 503:
      Toast.show({ content: '服务器过载', icon: 'fail' }); // unavaliable是有效的意思
      break;
    case 504:
      Toast.show({ content: '网关超时', icon: 'fail' }); // 504
      break;
    default:
      Toast.show({ content: defaultToast, icon: 'fail' });
      break;
  }
};

/**
 * 后端某些接口会出现另一种格式
 * 这里是临时的类型后续可能会移除
 */
interface ResponseStructure2 {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
}

const resTypeConversion = (res: ResponseStructure | ResponseStructure2) => {
  try {
    if ('code' in res) return res;
    return {
      code: res.status,
      message: res.message,
      data: null,
    };
  } catch (e) {
    console.log(e);
  }
};

// 当前请求个数
let reqLoadingNum: number = 0;

/**
 * @name 拦截器
 * @doc https://umijs.org/docs/max/request#配置
 */
export const requestConfig = {
  timeout: 1000 * 100, //请求时间
  errorConfig: {
    // 响应码本身就异常会走这里
    errorHandler(err: any) {
      Toast.clear();
      if (err?.response?.status >= 400) {
        networkError(
          err?.response?.status,
          err?.response?.data?.message ?? '请求失败！',
        );
      } else if (err?.code === 'ECONNABORTED') {
        // 接口请求超时
        Toast.show({
          content: '请求超时！',
          duration: 1000,
        });
      }
      // throw new Error(err.response.data.message);
    },
  },
  // 请求拦截器
  requestInterceptors: [
    async (config: any) => {
      // 拦截请求配置，进行个性化处理。
      const { NODE_ENV } = process.env;
      //  支付相关接口处理  在header增加 Payment-Platform-Code
      const isPaymentService = config.url.indexOf('/payment-service/') > -1;
      const target = `https://api-${UMI_ENV}.izen.art/`;

      if (NODE_ENV === 'development') {
        config.url = config.url.replace(target, '/api/');
      }
      const headers = config.headers;
      const token = getToken();
      const stage = getStage();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (stage) {
        headers.STAGE = stage;
      }
      if (
        isPaymentService &&
        (config?.params?.paymentPlatformCode ||
          config?.data?.paymentPlatformCode)
      ) {
        headers['Payment-Platform-Code'] =
          config?.params?.paymentPlatformCode ||
          config?.data?.paymentPlatformCode;
      }
      const encrypted = getEncrypted();
      // 拦截请求配置，boday 加密。
      if (config?.data && isApp() && encrypted === '1') {
        const res: any = await AppUtil.EncryptedJsonData(
          JSON.stringify(config?.data),
        );
        const data = JSON.parse(res?.encryptData);
        config.data = data;
      }
      console.log('url', config.url);
      let dId = deviceId();
      if (!isApp()) {
        dId = document.referrer || '';
      }
      const headeConfig = {
        'MSG-ID': getTransId(),
        'PLATFORM-TYPE': `${PLATFORMTYPE()}`,
        'DEVICE-ID': dId || '',
        'APP-VERSION': version() || '',
      };
      reqLoadingNum = reqLoadingNum + 1;
      if (reqLoadingNum === 1 && !headers.showLoading) {
        Toast.show({
          icon: <Boboloading />,
          maskClickable: false,
          duration: 0,
        });
      }
      return { ...config, headers: { ...headers, ...headeConfig } };
    },
  ],

  /**
   * 响应拦截器
   *
   * BUG: umi默认消费了响应拦截器的data属性
   * 这会导致响应拦截器中的return值和接口返回值不一致的问题，需要额外注意
   */
  responseInterceptors: [
    async (response: any) => {
      let data: any;
      // 判断是否需要解密
      if (response?.data?.encryptKey) {
        if (isApp()) {
          const res: any = await AppUtil.DecodeJsonData(
            JSON.stringify(response?.data),
          );
          data = resTypeConversion(JSON.parse(res?.decryptData));
          if (UMI_ENV !== 'prod') {
            console.log(
              JSON.parse(res?.decryptData),
              response?.config?.url,
              'app 返回数据',
            );
          }
          response.data = JSON.parse(res?.decryptData);
        } else {
          const res: any = decryptData(
            response?.data?.data,
            response?.data?.encryptKey,
          );
          data = resTypeConversion(res);
          response.data = res;
        }
      } else {
        data = resTypeConversion(response.data as any);
      }
      reqLoadingNum = reqLoadingNum - 1;
      if (reqLoadingNum <= 0) {
        Toast.clear();
      }
      if (data?.code) {
        if (data?.code === 401) {
          if (isApp()) {
            AppUtil.checkToken({ isOpenLogin: true });
          }
        }
        // 增加code白名单 不弹错误提示
        // notToast 处理滑块验证 不弹toast
        if (
          !codeWhiteList.includes(data.code) &&
          !response?.config?.url.includes('notToast=1')
        ) {
          Toast.clear();
          //滑块验证message 拦截
          let message = data.message;
          if (
            [
              PayCodeStatus.captchaErrorCode,
              PayCodeStatus.smsErrorCode,
            ].includes(data.code)
          ) {
            message = '滑块验证失败';
          }
          Toast.show({
            content: message ?? '请求失败！',
            duration: 2000,
          });
        }
      }
      // 注意: 这里返回res.data也就是ResponseStructure，umi会再返回一层data直接拿到真实数据T
      return response;
    },
  ],
};
